import { useEffect, useRef, useState } from "react";
import { api } from "../api.ts";
import {
  HALLAZGOS_IMAGEN, NOMBRE_TIPO_IMAGEN, AVISO_IA_IMAGENES, longitudAparente, distanciaPx, SENSORES_PERIAPICALES, escalaPorSensor, longitudPorPuntos,
  type HallazgoImagen, type TipoImagen, type MedidaImagen,
} from "../../../dominio/src/index.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";

interface Lectura { id: string; tipo_imagen: TipoImagen; calidad: string; resumen: string; hallazgos: HallazgoImagen[]; limitaciones: string; revision: Record<string, string>; creado: string; por?: string; revisado_por?: string | null }
interface Estado { tipoImagen: TipoImagen | null; autorizado: boolean; iaActiva: boolean; analisis: Lectura | null }
type Punto = { x: number; y: number };

const COLOR: Record<string, string> = { alta: "#d12b2b", media: "#d98a00", baja: "#2f7fd1" };

/**
 * Radiografía con ayuda de la IA: lectura sugerida (cada punto se acepta o se descarta) y medición de la longitud aparente con una referencia conocida.
 * La IA solo sugiere; lo que se acepta queda registrado, pero no pasa solo a la historia clínica.
 */
export function AnalisisRadiografia({ fotoId, url, etiqueta, alCerrar }: { fotoId: string; url: string; etiqueta: string; alCerrar: () => void }) {
  const [e, setE] = useState<Estado | null>(null);
  const [lectura, setLectura] = useState<Lectura | null>(null);
  const [foco, setFoco] = useState<number | null>(null);
  const [medir, setMedir] = useState(false);
  const [refMm, setRefMm] = useState("");
  const [puntos, setPuntos] = useState<Punto[]>([]);
  const [tam, setTam] = useState<{ w: number; h: number } | null>(null);
  // longitud automática: los dos puntos que propone la IA (corona y ápice) se pueden mover; la escala sale del tamaño del sensor
  const [ajustes, setAjustes] = useState<Record<number, MedidaImagen>>({});
  const [arrastre, setArrastre] = useState<{ i: number; p: "a" | "b" } | null>(null);
  const [sensor, setSensorEstado] = useState(() => { try { return localStorage.getItem("donna.sensor") ?? "2"; } catch { return "2"; } });
  const setSensor = (v: string) => { setSensorEstado(v); try { localStorage.setItem("donna.sensor", v); } catch { /* sin almacenamiento */ } };
  const img = useRef<HTMLImageElement>(null);
  const cerrar = useRef<HTMLButtonElement>(null);
  const a = useAccion();
  const ruta = `/api/fotos/${fotoId}/analisis`;
  const cargar = async () => { const r = await a.correr(() => api<Estado>("GET", ruta)); if (r) { setE(r); setLectura(r.analisis); } };
  useEffect(() => { cargar(); cerrar.current?.focus(); }, [fotoId]);

  const [piezasTxt, setPiezasTxt] = useState("");
  async function pedir() {
    const piezas = [...new Set(piezasTxt.split(/[^\d]+/).filter(Boolean).map(Number))];
    const r = await a.correr(() => api<Lectura>("POST", `${ruta}-ia`, piezas.length ? { piezas } : {}));
    if (r) setLectura({ ...r, revision: {} });
  }
  async function revisar(i: number, estado: "aceptado" | "descartado") {
    if (!lectura) return;
    const nuevo = lectura.revision[String(i)] === estado ? null : estado;
    const r = await a.correr(() => api<{ revision: Record<string, string> }>("POST", `/api/analisis-ia/${lectura.id}/revision`, { indice: i, estado: nuevo }));
    if (r) setLectura({ ...lectura, revision: r.revision });
  }
  function tocar(ev: React.MouseEvent<HTMLDivElement>) {
    if (!medir || !tam || !img.current) return;
    const r = img.current.getBoundingClientRect();
    const p = { x: ((ev.clientX - r.left) / r.width) * tam.w, y: ((ev.clientY - r.top) / r.height) * tam.h };
    setPuntos((v) => (v.length >= 4 ? [p] : [...v, p]));
  }
  useEffect(() => { setAjustes({}); }, [lectura?.id]);
  const medidas = (lectura?.hallazgos ?? []).flatMap((h, i) => (h.tipo === "longitud_aparente" && h.medida && lectura?.revision[String(i)] !== "descartado" ? [{ i, h, m: ajustes[i] ?? h.medida }] : []));
  const escala = tam ? escalaPorSensor(tam.w, tam.h, sensor) : null;
  useEffect(() => {
    if (!arrastre) return;
    const mover = (ev: PointerEvent) => {
      const r = img.current?.getBoundingClientRect(); const base = lectura?.hallazgos[arrastre.i]?.medida;
      if (!r || !base) return;
      const p = { x: Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (ev.clientY - r.top) / r.height)) };
      setAjustes((v) => ({ ...v, [arrastre.i]: { ...(v[arrastre.i] ?? base), [arrastre.p]: p } }));
    };
    const soltar = () => setArrastre(null);
    addEventListener("pointermove", mover); addEventListener("pointerup", soltar); addEventListener("pointercancel", soltar);
    return () => { removeEventListener("pointermove", mover); removeEventListener("pointerup", soltar); removeEventListener("pointercancel", soltar); };
  }, [arrastre, lectura?.id]);
  const refPx = puntos.length >= 2 ? distanciaPx(puntos[0], puntos[1]) : 0;
  const medidaPx = puntos.length >= 4 ? distanciaPx(puntos[2], puntos[3]) : 0;
  const mm = longitudAparente(Number(refMm.replace(",", ".")), refPx, medidaPx);
  const paso = puntos.length < 2 ? "1. Toca los dos extremos de la referencia (por ejemplo una lima con tope de largo conocido)." : puntos.length < 4 ? "2. Toca la corona (borde incisal u oclusal) y luego el ápice de la pieza." : "";
  const grosor = tam ? Math.max(tam.w, tam.h) / 300 : 3;

  return (
    <div className="visor visor-ia" role="dialog" aria-modal="true" aria-label={`Radiografía con ayuda de IA: ${etiqueta}`}>
      <div className="visor-ia-cuerpo">
        <div className="visor-ia-imagen" onClick={tocar} style={{ cursor: medir ? "crosshair" : "default" }}>
          <div className="visor-ia-marco">
            <img ref={img} src={url} alt={etiqueta} onLoad={(x) => setTam({ w: x.currentTarget.naturalWidth, h: x.currentTarget.naturalHeight })} />
            {tam && (
              <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="visor-ia-zonas" aria-hidden="true">
                {lectura?.hallazgos.map((h, i) => h.zona && lectura.revision[String(i)] !== "descartado" && (
                  <rect key={i} x={h.zona.x} y={h.zona.y} width={h.zona.w} height={h.zona.h} fill="none" stroke={COLOR[h.confianza]} strokeWidth={foco === i ? 0.006 : 0.003} vectorEffect="non-scaling-stroke"
                    style={{ strokeWidth: foco === i ? 3 : 1.6, opacity: foco === null || foco === i ? 1 : 0.45 }} />
                ))}
              </svg>
            )}
            {tam && medidas.length > 0 && !medir && (
              <svg viewBox={`0 0 ${tam.w} ${tam.h}`} preserveAspectRatio="none" className="visor-ia-zonas" aria-hidden="true">
                {medidas.map(({ i, m }) => (
                  <g key={i}>
                    <line x1={m.a.x * tam.w} y1={m.a.y * tam.h} x2={m.b.x * tam.w} y2={m.b.y * tam.h} stroke="#ffd23f" strokeWidth={grosor} strokeDasharray={`${grosor * 4} ${grosor * 2}`} />
                    {(["a", "b"] as const).map((k) => (
                      <circle key={k} cx={m[k].x * tam.w} cy={m[k].y * tam.h} r={grosor * 2.6} fill={k === "a" ? "#18b27a" : "#ffd23f"} stroke="#000" strokeWidth={grosor * 0.5}
                        style={{ pointerEvents: "all", cursor: "grab", touchAction: "none" }} onPointerDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); setArrastre({ i, p: k }); }} />
                    ))}
                  </g>
                ))}
              </svg>
            )}
            {tam && medir && (
              <svg viewBox={`0 0 ${tam.w} ${tam.h}`} preserveAspectRatio="none" className="visor-ia-zonas" aria-hidden="true">
                {puntos.length >= 2 && <line x1={puntos[0].x} y1={puntos[0].y} x2={puntos[1].x} y2={puntos[1].y} stroke="#18b27a" strokeWidth={grosor} />}
                {puntos.length >= 4 && <line x1={puntos[2].x} y1={puntos[2].y} x2={puntos[3].x} y2={puntos[3].y} stroke="#ffd23f" strokeWidth={grosor} />}
                {puntos.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={grosor * 2.2} fill={i < 2 ? "#18b27a" : "#ffd23f"} />)}
              </svg>
            )}
          </div>
        </div>
        <aside className="visor-ia-lado card">
          <div className="fila" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: 17 }}>{e?.tipoImagen ? NOMBRE_TIPO_IMAGEN[e.tipoImagen] : "Radiografía"}</h2>
            <button ref={cerrar} className="btn chico" onClick={alCerrar}>Cerrar</button>
          </div>
          <p className="muted" style={{ margin: "4px 0", fontSize: 12 }}>{AVISO_IA_IMAGENES}</p>
          <AvisoError error={a.error} />

          {e && e.tipoImagen && !lectura && (
            e.autorizado && e.iaActiva
              ? (
                <>
                  <label className="campo">¿Qué pieza o piezas muestra? (recomendado)
                    <input className="entrada mono" inputMode="numeric" value={piezasTxt} maxLength={30} placeholder="Ej. 36 o 46, 47" onChange={(x) => setPiezasTxt(x.target.value)} style={{ maxWidth: 200 }} aria-describedby="ayuda-piezas" />
                    <small id="ayuda-piezas" className="muted">Si la imagen viene volteada o rotada, la IA puede confundir la arcada o el lado. Con la pieza indicada, Donna usa tu numeración y descarta lo que la IA ubique en otro cuadrante.</small>
                  </label>
                  <button className="btn pri" onClick={pedir} disabled={a.ocupado}>{a.ocupado ? "Leyendo la imagen…" : "Pedir lectura a la IA"}</button>
                </>
              )
              : <div className="aviso">{!e.iaActiva ? "La IA no está activada en este servidor." : "El paciente no ha autorizado el análisis de sus imágenes con IA. Se registra en Ficha, Datos personales."}</div>
          )}
          {e && !e.tipoImagen && <div className="aviso">La IA lee periapicales, panorámicas y aletas de mordida. Marca la foto con esa vista para usarla. La medición sí está disponible.</div>}

          {lectura && (
            <div className="ia-lectura">
              <p style={{ margin: "6px 0" }}><b>Calidad de la imagen:</b> {lectura.calidad}. {lectura.resumen}</p>
              {lectura.hallazgos.length === 0 && <p className="muted">La IA no señaló puntos para revisar.</p>}
              <ul className="ia-puntos">
                {lectura.hallazgos.map((h, i) => {
                  const rev = lectura.revision[String(i)];
                  return (
                    <li key={i} className={rev ?? ""} onMouseEnter={() => setFoco(i)} onMouseLeave={() => setFoco(null)}>
                      <div><i style={{ background: COLOR[h.confianza] }} aria-hidden="true" /> <b>{HALLAZGOS_IMAGEN[h.tipo].nombre}</b>{h.pieza ? ` · pieza ${h.pieza}` : ""} <small className="muted">· confianza {h.confianza}</small></div>
                      <div>{h.descripcion}</div>
                      <div className="fila">
                        <button className="btn chico" aria-pressed={rev === "aceptado"} onClick={() => revisar(i, "aceptado")}>{rev === "aceptado" ? "✓ Aceptado" : "Lo veo también"}</button>
                        <button className="btn chico" aria-pressed={rev === "descartado"} onClick={() => revisar(i, "descartado")}>{rev === "descartado" ? "✕ Descartado" : "Descartar"}</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {lectura.limitaciones && <p className="muted" style={{ fontSize: 12 }}>Limitaciones: {lectura.limitaciones}</p>}
              <small className="muted">Cada punto aceptado queda registrado en la auditoría; pásalo a la historia clínica y al odontograma solo si lo confirmas con tu examen.</small>
              <div><span className="fila" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                <input className="entrada mono" inputMode="numeric" value={piezasTxt} maxLength={30} placeholder="Pieza (ej. 36)" aria-label="Pieza o piezas que muestra la radiografía" onChange={(x) => setPiezasTxt(x.target.value)} style={{ maxWidth: 140 }} />
                <button className="btn chico" onClick={pedir} disabled={a.ocupado || !e?.autorizado}>Volver a leer</button>
              </span></div>
            </div>
          )}

          {medidas.length > 0 && (
            <div className="ia-medir">
              <div className="eyebrow">Longitud aparente automática</div>
              <label className="campo">Tamaño de la película o sensor
                <select className="entrada" value={sensor} onChange={(x) => setSensor(x.target.value)}>{SENSORES_PERIAPICALES.map((t) => <option key={t.clave} value={t.clave}>{t.nombre}</option>)}</select>
              </label>
              {medidas.map(({ i, h, m }) => {
                const mm = escala && tam ? longitudPorPuntos(m, tam.w, tam.h, escala) : null;
                return (
                  <p key={i} style={{ margin: "4px 0" }}>
                    {h.pieza ? `Pieza ${h.pieza}: ` : "Pieza: "}<b>{mm !== null ? `≈ ${String(mm).replace(".", ",")} mm` : "sin escala"}</b>
                    {ajustes[i] && <small className="muted"> · ajustada por ti</small>}
                  </p>
                );
              })}
              {!escala && tam && <p className="muted" style={{ margin: "4px 0" }}>La imagen no tiene la proporción del sensor elegido (¿está recortada?). Elige otro tamaño o usa la medición con una referencia, más abajo.</p>}
              <small className="muted">Los puntos verde (borde o cúspide) y amarillo (ápice) los propone la IA: arrástralos hasta donde corresponda. La escala supone que la imagen es el sensor completo y no corrige la distorsión; es una medida aparente. La longitud de trabajo se confirma con el localizador apical y la conductometría.</small>
            </div>
          )}

          <div className="eyebrow">Longitud aparente (endodoncia)</div>
          <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={medir} onChange={(x) => { setMedir(x.target.checked); setPuntos([]); }} /> Medir sobre la imagen</label>
          {medir && (
            <div className="ia-medir">
              <label className="campo">Largo real de la referencia (mm)<input inputMode="decimal" value={refMm} onChange={(x) => setRefMm(x.target.value)} placeholder="Ej. 25" style={{ maxWidth: 120 }} /></label>
              {paso && <p style={{ margin: "4px 0" }}>{paso}</p>}
              {mm !== null && <p style={{ margin: "4px 0" }}><b>Longitud aparente: {String(mm).replace(".", ",")} mm</b></p>}
              {puntos.length >= 4 && mm === null && <p className="muted">Escribe el largo real de la referencia en milímetros.</p>}
              <small className="muted">Es una medida aparente: la radiografía distorsiona. La longitud de trabajo se confirma con el localizador apical y la conductometría.</small>
              <div><button className="btn chico" onClick={() => setPuntos([])}>Marcar de nuevo</button></div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
